import React, { useMemo, useState } from "react";

export interface Column<T> {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  /** Render the cell. Falls back to String(row[key]). */
  render?: (row: T, index: number) => React.ReactNode;
  /** Value used for sorting; defaults to row[key]. */
  sortValue?: (row: T) => number | string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  /** Column key to sort by initially (descending). */
  initialSortKey?: string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  rowKey,
  onRowClick,
  pageSize,
  initialSortKey,
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<string | undefined>(initialSortKey);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;
    const getValue = col.sortValue ?? ((row: T) => row[sortKey]);
    return [...rows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, columns, sortKey, sortDir]);

  const totalPages = pageSize ? Math.ceil(sortedRows.length / pageSize) : 1;
  const clampedPage = Math.min(page, Math.max(0, totalPages - 1));
  const visibleRows = pageSize
    ? sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize)
    : sortedRows;

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(0);
  };

  return (
    <div>
      <div className="an-table-scroll">
        <table className="an-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={
                    col.align === "right"
                      ? "an-align-right"
                      : col.align === "center"
                        ? "an-align-center"
                        : ""
                  }
                  style={{
                    cursor: col.sortValue || col.key in (rows[0] || {}) ? "pointer" : "default",
                  }}
                  onClick={() => toggleSort(col.key)}
                >
                  {col.header}
                  {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                className={onRowClick ? "an-table-row--clickable" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={
                      col.align === "right"
                        ? "an-align-right"
                        : col.align === "center"
                          ? "an-align-center"
                          : ""
                    }
                  >
                    {col.render ? col.render(row, index) : String(row[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageSize && totalPages > 1 && (
        <div className="an-table-pagination">
          <button
            className="an-button"
            disabled={clampedPage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span>
            Page {clampedPage + 1} of {totalPages}
          </span>
          <button
            className="an-button"
            disabled={clampedPage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default DataTable;
